import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('vrs_token');
    const storedUser = localStorage.getItem('vrs_user');

    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);

        // Verify token is still valid
        try {
          const response = await authAPI.getMe();
          setUser(response.data);
          localStorage.setItem('vrs_user', JSON.stringify(response.data));
        } catch {
          // [Phase 3(c) Stage 1, §8] A failed /me means the stored token is no
          // longer valid — clear stored auth state instead of silently keeping
          // a stale user. (A 401 also triggers the api interceptor, which sets
          // the session-expired flag and redirects.)
          localStorage.removeItem('vrs_token');
          localStorage.removeItem('vrs_user');
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch {
        localStorage.removeItem('vrs_token');
        localStorage.removeItem('vrs_user');
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Session timeout: 30 minutes of inactivity
  useEffect(() => {
    if (!isAuthenticated) return;
    let timeout;
    const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

    const resetTimer = () => {
      clearTimeout(timeout);
      localStorage.setItem('vrs_last_activity', Date.now().toString());
      timeout = setTimeout(() => {
        localStorage.removeItem('vrs_token');
        localStorage.removeItem('vrs_user');
        // [Phase 3(c) Stage 1, §8] Flag the expiry so the login page can show
        // "session expired — please sign in again" rather than bouncing silently.
        sessionStorage.setItem('vrs_session_expired', '1');
        setUser(null);
        setIsAuthenticated(false);
        window.location.href = '/login';
      }, SESSION_TIMEOUT);
    };

    // Check if session already expired on load
    const lastActivity = parseInt(localStorage.getItem('vrs_last_activity') || '0');
    if (lastActivity && Date.now() - lastActivity > SESSION_TIMEOUT) {
      localStorage.removeItem('vrs_token');
      localStorage.removeItem('vrs_user');
      // [Phase 3(c) Stage 1, §8] Flag the expiry so the login page explains it.
      sessionStorage.setItem('vrs_session_expired', '1');
      setUser(null);
      setIsAuthenticated(false);
      return;
    }

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => document.addEventListener(e, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(timeout);
      events.forEach(e => document.removeEventListener(e, resetTimer));
    };
  }, [isAuthenticated]);

  const login = useCallback((token, userData) => {
    localStorage.setItem('vrs_token', token);
    localStorage.setItem('vrs_user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch {
      // ignore
    }
    localStorage.removeItem('vrs_token');
    localStorage.removeItem('vrs_user');
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem('vrs_user', JSON.stringify(userData));
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, login, logout, checkAuth, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
